import { Card } from 'antd';
import { getProductUpdateInfoNew } from 'apis/pms';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import ProductCreateForm from '../create-page-v2/product-create-form';
import { UpdateProductFormInitData } from './types';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import ProductStockStatusHistory from '../product-stock-status-history';
import i18n from 'i18n';
import ProductActivityList from 'components/product-add-edit/product-activity-list';
import LOCALS from 'commons/locals';
import { History } from 'lucide-react';
import { ProductUpdateInfoNewType } from 'types/pms';
import ProductBaseInfo, {
  ProductBaseInfoProps,
} from 'components/product-add-edit/base-info';
import { priceToWithoutTax } from 'utils/price-change';
import { fetchRecycleOrderDetail } from 'apis/oms';
import { UnwrapPromise } from 'types/base';
import useResource from 'commons/hooks/useResource';
import { getProductTaxRate } from 'utils/stock-place-currency';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import { ProductTaxRegime } from 'constants/france-tax';

const ProductUpdatePageV2 = ({ viewMode = false }: { viewMode?: boolean }) => {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { userId: adminId } = useSelector(selectUserInfo);
  const [initData, setInitData] = useState<UpdateProductFormInitData>();
  const { productCategoryCascaderOptions, materialCascaderOptionsMap } =
    useAppSelector(selectGlobalInfo);
  const [pmsProductActivityList, setPmsProductActivityList] = useState<
    ProductUpdateInfoNewType['pmsProductActivityList']
  >([]);
  const [productBaseInfo, setProductBaseInfo] =
    useState<ProductBaseInfoProps>();

  const [recycleOrderDetail, setRecycleOrderDetail] =
    useState<
      UnwrapPromise<ReturnType<typeof fetchRecycleOrderDetail>>['data']
    >();

  const canEditCostPrice = useResource('product-cost-price-edit');
  const canEditCareCost = useResource('product-care-cost-edit');
  const canEditSourceType = useResource('product-source-type-edit');

  useEffect(() => {
    if (isNaN(productId)) {
      return;
    }

    if (
      !Object.keys(materialCascaderOptionsMap).length ||
      productCategoryCascaderOptions.length === 0
    ) {
      return;
    }

    getProductUpdateInfoNew(productId).then((res) => {
      const {
        owner,
        productCategoryId,
        boxId,
        material,
        extendType,
        i18nList,
        stockPlace,
        brandName,
        price,
        costPrice,
        careCost,
        currency,
        referencePrice,
        skuType,
        sourceType,
        stock,
        promotionType,
        originalPrice,
        description,
        remark,
        rank,
        colors,
        color,
        colorRemark,
        materialRemark,
        hardware,
        hardwareRemark,
        stamp,
        stampRemark,
        fullStamp,
        accessory,
        collections,
        facebookEnabled,
        albumPics,
        pmsProductActivityList,
        recycleOrder,
        publishStatus,
        stockStatus,
        orderId,
        soldTime,
        priceHkd,
        taxRegime,
        internalImages,
        entryTime,
        sourceProductId,
      } = res;

      const taxRate = getProductTaxRate(stockPlace, currency);

      // 商品分类查找完整路径
      let productCategoryIdList: number[] = [];
      let materialArr: string[][] = [];
      if (productCategoryId) {
        const target = findCascaderOptionById(
          productCategoryId,
          productCategoryCascaderOptions,
        );

        if (target && target.treeIds) {
          productCategoryIdList = target.treeIds?.split(',').map((i) => +i);

          if (productCategoryIdList.length) {
            const productCategoryIdFirstLevel = productCategoryIdList[0];
            const materialCascaderOptions =
              [...materialCascaderOptionsMap[productCategoryIdFirstLevel]].sort(
                (a, b) => a.sort! - b.sort!,
              ) || [];

            // 商品材质选项依赖于商品分类

            if (material) {
              const tempArr = material.split(',').filter(Boolean);

              materialArr = tempArr.map((material) => {
                const target = findCascaderOptionById(
                  material,
                  materialCascaderOptions,
                );
                const tempStrArr = target?.treeIds?.split(',').filter(Boolean);
                return tempStrArr || [];
              });
            }
          }
        }
      }

      setInitData({
        owner: owner ?? undefined,
        productCategoryIdList,
        boxId: boxId ?? null,
        extendType,
        name: {
          en: res.name,
          ja: i18nList?.find((i) => i.lang === 'ja')?.name || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.name || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.name || '',
        },
        stockPlace,
        brandName: brandName || 'Hermes',
        price,
        priceWithoutTax: priceToWithoutTax(price, taxRate),
        costPrice,
        careCost: careCost ?? null,
        currency,
        referencePrice: referencePrice ?? null,
        skuType,
        sourceType,
        stock,
        promotionType,
        originalPrice: originalPrice ?? null,
        description: description ?? '',
        remark: remark ?? '',
        rank,
        rankDesc: {
          en: res.rankDescription || '',
          ja: i18nList?.find((i) => i.lang === 'ja')?.attrRankDesc || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.attrRankDesc || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.attrRankDesc || '',
        },
        colors: colors ?? undefined,
        color: color?.split(',').filter(Boolean),
        colorRemark: colorRemark ?? '',
        material: materialArr,
        materialRemark: materialRemark ?? '',
        hardware: hardware?.split(',').filter(Boolean),
        hardwareRemark: hardwareRemark ?? '',
        stamp: stamp ?? undefined,
        stampRemark: stampRemark ?? '',
        fullStamp: fullStamp ?? '',
        accessory: accessory?.split(',').filter(Boolean),
        size: {
          en: res.size || '',
          ja: i18nList?.find((i) => i.lang === 'ja')?.attrSize || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.attrSize || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.attrSize || '',
        },
        subTitle: {
          en: res.subTitle || '',
          ja: i18nList?.find((i) => i.lang === 'ja')?.subTitle || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.subTitle || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.subTitle || '',
        },
        note: {
          en: res.note || '',
          ja: i18nList?.find((i) => i.lang === 'ja')?.note || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.note || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.note || '',
        },
        detailTitle: {
          en: res.detailTitle || '',
          ja: i18nList?.find((i) => i.lang === 'ja')?.detailTitle || '',
          zh_CN: i18nList?.find((i) => i.lang === 'zh_CN')?.detailTitle || '',
          zh_TW: i18nList?.find((i) => i.lang === 'zh_TW')?.detailTitle || '',
        },
        collections: collections?.split(',').filter(Boolean),
        facebookEnabled,
        albumPics: albumPics?.split(',').filter(Boolean),
        priceHkd,
        taxRegime:
          taxRegime ??
          (stockPlace === STOCK_PLACE_MAP.FRANCE
            ? ProductTaxRegime.STANDARD_TVA
            : null),
        internalImages: internalImages || [],
      });
      setPmsProductActivityList(pmsProductActivityList);
      setProductBaseInfo({
        productId,
        sourceProductId: sourceProductId,
        recycleOrderEmail: recycleOrder?.email,
        recycleOrderMemberId: recycleOrder?.memberId,
        recycleOrderId: recycleOrder?.id,
        recycleOrderCode: recycleOrder?.code,
        publishStatus: publishStatus,
        stockStatus: stockStatus,
        soldOrderId: orderId,
        soldTime: soldTime,
        entryTime: entryTime,
      });

      if (recycleOrder?.id) {
        fetchRecycleOrderDetail({ id: recycleOrder.id }).then((res) => {
          setRecycleOrderDetail(res.data);
        });
      }
    });
  }, [materialCascaderOptionsMap, productCategoryCascaderOptions, productId]);

  const minCostPrice = useMemo(() => {
    if (!recycleOrderDetail) return 0;
    if (!recycleOrderDetail.omsRecycleOrderItem) return 0;

    const { finalRecyclePrice, finalSalePrice } =
      recycleOrderDetail.omsRecycleOrderItem;
    return finalRecyclePrice || finalSalePrice || 0;
  }, [recycleOrderDetail]);

  // 一定要等到基本数据都准备完成了，再渲染 form 表单
  // 否则 form 的 defaultValues 会不正确
  if (
    isNaN(productId) ||
    !adminId ||
    !initData ||
    !productCategoryCascaderOptions.length ||
    !Object.keys(materialCascaderOptionsMap).length
  ) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Card title={i18n.t(LOCALS.edit_product)} bodyStyle={{ padding: '16px' }}>
        <ProductBaseInfo {...productBaseInfo} />
        <ProductCreateForm
          productId={productId}
          initData={initData}
          disabledCostPrice={!canEditCostPrice}
          disabledCareCost={!canEditCareCost}
          minCostPrice={minCostPrice}
          canEditSourceType={
            canEditSourceType && !productBaseInfo?.recycleOrderId
          }
          viewMode={viewMode}
        />
      </Card>

      {!!id && (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5" />
            <div className="text-base font-medium">
              {i18n.t(LOCALS.rrooMUILfK)}
            </div>
          </div>
          <ProductStockStatusHistory productId={productId} />
        </Card>
      )}

      <ProductActivityList
        id={productId}
        pmsProductActivityList={pmsProductActivityList}
      />
    </div>
  );
};

export default ProductUpdatePageV2;
